import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  Download,
  Share2,
  Eye,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  File,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import { usePageMeta } from "../../hooks/usePageMeta";
import { PAGE_METADATA } from "../../config/pageMetadata";
import { downloadFromResponse } from "../../utils/fileDownload";
import type { Case } from "../../types";
import { Table, TableColumn } from "../../components/common/tables/Table";
import { TableContainer } from "../../components/common/tables/TableContainer";
import {
  IconWithTextCell,
  LinkCell,
  DateCell,
  ActionsCell,
} from "../../components/common/tables/cells";
import {
  CreateShareLinkModal,
  CreateShareLinkParams,
} from "../../components/ShareLinks/CreateShareLinkModal";
import { useOrg } from "../../contexts/OrgContext.tsx";

interface FileManagementProps {}

interface FileWithCase {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  caseId: string;
  caseTitle?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

const getFileIcon = (fileType: string) => {
  if (fileType.startsWith("image/")) return ImageIcon;
  if (fileType.startsWith("video/")) return Film;
  if (fileType.startsWith("audio/")) return Music;
  if (fileType.includes("pdf")) return FileText;
  if (fileType.includes("zip") || fileType.includes("rar")) return Archive;
  return File;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const FileManagement: React.FC<FileManagementProps> = () => {
  usePageMeta(PAGE_METADATA.files);
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const [files, setFiles] = useState<FileWithCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [_cases, setCases] = useState<Case[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileWithCase | null>(null);
  const { orgId, navigateOrg, navigateOrgUrl } = useOrg();

  useEffect(() => {
    if (orgId) {
      loadCasesAndFiles();
    }
  }, [orgId]);

  const loadCasesAndFiles = async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      // Load all cases for this org
      const casesList = await apiClient.listCases(orgId);
      setCases(casesList);

      // Load all files for all cases
      const allFiles: FileWithCase[] = [];
      for (const caseItem of casesList) {
        try {
          const caseFiles = await apiClient.listFilesByCase(caseItem.id);
          const filesWithCase = caseFiles.map((file: any) => ({
            id: file.id,
            filename: file.filename || file.original_filename,
            mimeType: file.mimeType || file.mime_type,
            size: file.size || file.size_bytes,
            caseId: file.caseId || file.case_id,
            caseTitle: caseItem.title,
            uploadedAt: file.uploadedAt || file.created_at,
            uploadedBy: file.uploadedBy || file.uploaded_by,
          }));
          allFiles.push(...filesWithCase);
        } catch (err) {
          console.error(`Failed to load files for case ${caseItem.id}:`, err);
        }
      }

      // Sort by upload date (newest first)
      allFiles.sort(
        (a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
      );
      setFiles(allFiles);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to load data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      const response = await apiClient.downloadFileResponse(fileId);
      await downloadFromResponse(response, filename);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to download file",
      });
    }
  };

  const handleShareFile = (file: FileWithCase) => {
    setSelectedFile(file);
    setShowShareModal(true);
  };

  const handleCreateShareLink = async (params: CreateShareLinkParams) => {
    if (!selectedFile) return { publicUrl: "", id: "" };

    const result = await apiClient.createShareLink({
      resourceType: "file",
      resourceId: selectedFile.id,
      ...params,
    });

    return {
      publicUrl: result.publicUrl,
      id: result.id,
    };
  };

  const handleViewFile = (fileId: string) => {
    navigateOrg(`/files/${fileId}`);
  };

  const columns: TableColumn<FileWithCase>[] = [
    {
      header: "File Name",
      accessor: "filename",
      render: (value: string, row: FileWithCase) => (
        <IconWithTextCell
          icon={getFileIcon(row.mimeType)}
          iconColor="brand"
          primaryText={value}
          primaryLink={navigateOrgUrl(`/files/${row.id}`)}
          secondaryText={formatFileSize(row.size)}
        />
      ),
    },
    {
      header: "Case",
      accessor: "caseTitle",
      render: (value: string, row: FileWithCase) => (
        <LinkCell
          to={navigateOrgUrl(`/cases/${row.caseId}`)}
          text={value}
          fallback="Unknown Case"
        />
      ),
    },
    {
      header: "Uploaded",
      accessor: "uploadedAt",
      render: (value: string) => <DateCell date={value} />,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_: string, row: FileWithCase) => (
        <ActionsCell
          items={[
            {
              label: "View Details",
              icon: <Eye className="w-4 h-4" />,
              onClick: () => handleViewFile(row.id),
            },
            {
              label: "Download",
              icon: <Download className="w-4 h-4" />,
              onClick: () => handleDownloadFile(row.id, row.filename),
            },
            {
              label: "Share",
              icon: <Share2 className="w-4 h-4" />,
              onClick: () => handleShareFile(row),
            },
          ]}
        />
      ),
    },
  ];
  console.log("files.lenght", files.length);
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <Upload size={32} />
            File Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage and share encrypted files for cases
          </p>
        </div>
      </div>

      {/* Files Table */}
      <TableContainer
        loading={loading}
        loadingText="Loading files..."
        isEmpty={files.length === 0}
        emptyIcon={<File size={48} />}
        emptyTitle={`No files, yet.`}
        title="All Files"
        titleIcon={<FileText size={20} />}
        count={files.length}
      >
        <Table columns={columns} data={files} />
      </TableContainer>

      {/* Share Link Modal */}
      <CreateShareLinkModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedFile(null);
        }}
        onCreate={handleCreateShareLink}
        resourceType="file"
        onViewAllLinks={() => {
          if (selectedFile) {
            navigate(`/files/${selectedFile.id}`);
          }
        }}
      />
    </div>
  );
};
